'use client';

/**
 * Тема оформления.
 *
 * Три состояния, а не два: «светлая», «тёмная» и «как в системе». Последнее
 * — состояние по умолчанию и отдельная сущность: человек, не трогавший
 * переключатель, должен следовать за настройкой ОС, а тронувший — нет,
 * даже если ОС переключится ночью.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

export const THEME_KEY = 'callog.theme';

interface ThemeContextValue {
  theme: Theme;
  /** Что показано на экране прямо сейчас — с учётом системной настройки. */
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored(): Theme {
  if (typeof window === 'undefined') return 'system';
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Класс на <html> — тот же признак, по которому Tailwind включает `dark:`. */
function apply(resolved: 'light' | 'dark') {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  // Подсказка браузеру: по ней он красит полосы прокрутки и системные поля.
  document.documentElement.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('system');
  const [resolved, setResolved] = useState<'light' | 'dark'>('light');

  // Первое чтение — после монтирования: на сервере ни localStorage, ни
  // системной настройки нет, а до этого момента класс уже проставлен
  // скриптом в разметке, поэтому мигания не происходит.
  useEffect(() => {
    const stored = readStored();
    setThemeState(stored);
    const next = stored === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : stored;
    setResolved(next);
    apply(next);
  }, []);

  // За системой следим, только пока выбран режим «как в системе».
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = media.matches ? 'dark' : 'light';
      setResolved(next);
      apply(next);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      if (next === 'system') window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, next);
    } catch {
      // Хранилище недоступно — в этой вкладке тема всё равно переключится.
    }
    const value = next === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : next;
    setResolved(value);
    apply(value);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme вызван вне ThemeProvider');
  return value;
}

/**
 * Скрипт, выставляющий тему до первой отрисовки.
 *
 * Без него страница успевает мигнуть белым перед тем, как React смонтируется
 * и переключит класс. Поэтому он идёт строкой в разметке, а не в компоненте:
 * выполниться нужно раньше, чем браузер что-либо покажет.
 */
export const themeBootstrapScript = `
(function () {
  try {
    var stored = localStorage.getItem('${THEME_KEY}');
    var dark = stored === 'dark' || (!stored && matchMedia('(prefers-color-scheme: dark)').matches);
    if (dark) document.documentElement.classList.add('dark');
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  } catch (e) {}
})();
`;
